// React
import React, { useEffect, useState } from "react";

// Libraries
import PropTypes from "prop-types";
import { Routes, Route } from "react-router-dom";
import { Dimmer } from "semantic-ui-react";
import { isEmpty, has } from "lodash"; // has
import { ReactNotifications } from "react-notifications-component";
import { useIdleTimer } from "react-idle-timer";

// Redux
import { connect } from "react-redux";
import { dismissNotification } from "@Actions/notifications";
import { logout, rehydrate } from "@Actions/authentication";

// Components
import ProtectedRoute from "@Components/ProtectedRoute";
import PublicRoute from "@Components/PublicRoute";
import NotificationDialog from "@Modals/NotificationDialog";
import Loading from "@Modals/Loading";
import CallWidget from "@Components/CallWidget";

// Features
import NoMatch from "@Features/NoMatch";
import Home from "@Features/Home";
import Authentication from "@Features/Authentication";
import ChooseCompany from "@Features/ChooseCompany";
import Monitor from "@Features/Monitor";
import Profiles from "@Features/Profiles";
import Groups from "@Features/Group";
import Invitations from "@Features/Invitations";
import Users from "@Features/Users";
import Category from "@Features/Category";
import Contact from "@Features/Contact";
import PresetMessage from "@Features/PresetMessage";
import Tags from "@Features/Tags";
import Hashtag from "@Features/Hashtag";
import QuickAnswers from "@Features/QuickAnswers";
import Dashboard from "@Features/Dashboard";
import ContactGroups from "@Features/ContactGroups";
import TableActionUser from "@Features/TableActionUser";
import ReportClosed from "@Features/ReportClosed";
import TimesReport from "@Features/TimesReport";
import GeneralReport from "@Features/GeneralReport";
import AsignationReport from "@Features/AsignationReport";
import Menu from "@Features/Menu";
import CompanyMaster from "@Features/CompanyMaster";
import TypeCompany from "@Features/TypeCompany";
import ChatBots from "@Features/ChatsBots";
import Template from "@Features/Template";
import ContactLink from "@Features/ContactlLink";
import ActionsChatBot from "@Features/ActionsChatBot";
import SurveyMaster from "@Features/SurveyMaster";
import Reports from "@Features/Reports";
import ChatTagsReport from "@Features/ChatTagsReport";
import WebhooksMaster from "@Features/WebhooksMaster";
import WhatsAppFlow from "@Features/WhatsAppFlow";

const App = ({ dispatch, isLoggedIn, isLoading, isError, notificationText, isVisible, companyId, companySettings }) => {
	const [isReady, setIsReady] = useState(false);
	const timeout = isEmpty(companySettings) ? 30 : companySettings.sessionTimeout;
	useEffect(() => {
		window.name = "MaxMonitorWindow";
		dispatch(rehydrate());
		setIsReady(true);
	}, []);

	useEffect(() => {
		if (isLoggedIn && companyId > 0 && has(companySettings, "colors")) {
			document.documentElement.style.setProperty(`--primary`, `${companySettings?.colors?.primary}`);
			document.documentElement.style.setProperty(`--accent`, `${companySettings?.colors?.accent}`);
			document.documentElement.style.setProperty(`--accentDarker`, `${companySettings?.colors?.accentDarker}`);
			document.documentElement.style.setProperty(`--panel`, `${companySettings?.colors?.panel}`);
			document.documentElement.style.setProperty(`--text`, `${companySettings?.colors?.text}`);
			document.documentElement.style.setProperty(`--toggle`, `${companySettings?.colors?.toggle}`);
			document.documentElement.style.setProperty(`--toggleFalse`, `${companySettings?.colors?.toggleFalse}`);
			document.documentElement.style.setProperty(`--groupHeader`, `${companySettings?.colors?.groupHeader}`);
			document.documentElement.style.setProperty(`--panelModal`, `${companySettings?.colors?.panelModal}`);

			document.documentElement.style.setProperty(`--header`, `${companySettings?.colors?.header}`);
			document.documentElement.style.setProperty(`--fontsHeader`, `${companySettings?.colors?.fontsHeader}`);
			document.documentElement.style.setProperty(`--section`, `${companySettings?.colors?.section}`);
			document.documentElement.style.setProperty(`--fontSection`, `${companySettings?.colors?.fontSection}`);
			document.documentElement.style.setProperty(`--formPanel`, `${companySettings?.colors?.formPanel}`);
			document.documentElement.style.setProperty(`--buttons`, `${companySettings?.colors?.buttons}`);
			document.documentElement.style.setProperty(`--fontsButtons`, `${companySettings?.colors?.fontsButtons}`);
			document.documentElement.style.setProperty(`--fontsHover`, `${companySettings?.colors?.fontsHover}`);

			// ChatBot Colors
			document.documentElement.style.setProperty(`--groupFonts`, `${companySettings?.colors?.groupFonts}`);
			document.documentElement.style.setProperty(`--groupBack`, `${companySettings?.colors?.groupBack}`);

			document.documentElement.style.setProperty(`--headerFormulary`, `${companySettings?.colors?.headerFormulary}`);
			document.documentElement.style.setProperty(`--fontFormulary`, `${companySettings?.colors?.fontFormulary}`);
			document.documentElement.style.setProperty(`--buttonFormulary`, `${companySettings?.colors?.buttonFormulary}`);
			document.documentElement.style.setProperty(`--hoverButtonsFormulary`, `${companySettings?.colors?.hoverButtonsFormulary}`);
		}
	}, [isLoggedIn, companyId, companySettings]);

	const handleDismiss = () => {
		dispatch(dismissNotification());
	};

	const onActive = () => {
		if (isLoggedIn) idleTimer.reset();
	};

	const onIdle = () => {
		if (isLoggedIn) dispatch(logout({ withoutServer: false, auto: true }));
	};

	const idleTimer = useIdleTimer({
		onActive,
		onIdle,
		timeout: 1000 * 60 * timeout,
		debounce: 1000 * 6 * timeout,
		startOnMount: true,
	});

	useEffect(() => {
		idleTimer.reset();
	}, [timeout]);

	if (!isReady) return <div />;
	return (
		<Dimmer.Dimmable dimmed={isLoading || isError}>
			{isLoggedIn && <CallWidget />}
			<ReactNotifications />
			<Loading isFetching={isLoading} />
			<NotificationDialog isVisible={isVisible} isError={isError} text={notificationText} onDismiss={handleDismiss} />
			<Routes>
				<Route index element={<Home />} />
				<Route element={<PublicRoute isLoggedIn={isLoggedIn} hasCompany={companyId > 0} />}>
					<Route path="auth/*" element={<Authentication />} />
					<Route path="/contactLink/:token?" element={<ContactLink />} />
				</Route>
				<Route element={<ProtectedRoute isLoggedIn={isLoggedIn} />}>
					<Route path="chooseCompany" element={<ChooseCompany />} />
					<Route path="dashboard" element={<Dashboard />} />
					<Route path="monitor" element={<Monitor />} />
					<Route path="history" element={<Monitor isHistoric />} />
					<Route path="companyUsers" element={<TableActionUser />} />
					<Route path="plantillas" element={<Template />} />
					<Route path="maintenance">
						<Route path="groups" element={<Groups />} />
						<Route path="members" element={<Invitations />} />
						<Route path="users" element={<Users />} />
						<Route path="contacts" element={<Contact />} />
						<Route path="presetMessages" element={<PresetMessage />} />
						<Route path="hashtags" element={<Hashtag />} />
						<Route path="tags" element={<Tags />} />
						<Route path="quickAnswers" element={<QuickAnswers />} />
						<Route path="contactGroups" element={<ContactGroups />} />
					</Route>
					<Route path="master">
						<Route path="menu" element={<Menu />} />
						<Route path="companies" element={<CompanyMaster />} />
						<Route path="typeCompany" element={<TypeCompany />} />
						<Route path="actions" element={<ActionsChatBot />} />
						<Route path="profiles" element={<Profiles />} />
						<Route path="chatBots" element={<ChatBots />} />
						<Route path="categories" element={<Category />} />
						<Route path="surveys" element={<SurveyMaster />} />
						<Route path="webhooks" element={<WebhooksMaster />} />
						<Route path="whatsappFlow" element={<WhatsAppFlow />} />
					</Route>
					<Route path="agentTime" element={<TableActionUser />} />
					<Route path="reports">
						<Route path="closed" element={<ReportClosed />} />
						<Route path="times" element={<TimesReport />} />
						<Route path="general" element={<GeneralReport />} />
						<Route path="dayhour" element={<AsignationReport />} />
						<Route path="chatTagsReport" element={<ChatTagsReport />} />
					</Route>
					<Route path="reportsMaster" element={<Reports />} />
				</Route>

				<Route path="*" element={<NoMatch />} />
			</Routes>
		</Dimmer.Dimmable>
	);
};

App.propTypes = {
	dispatch: PropTypes.func,
	isLoggedIn: PropTypes.bool,
	isLoading: PropTypes.bool,
	isError: PropTypes.bool,
	notificationText: PropTypes.string,
	isVisible: PropTypes.bool,
	companyId: PropTypes.number,
	companySettings: PropTypes.object,
};

function mapStateToProps({ loading, notifications, user, company }) {
	return {
		isLoading: loading.isLoading,
		isError: notifications.isError,
		isVisible: notifications.isVisible,
		notificationText: notifications.text,
		isLoggedIn: user.isLoggedIn,
		companyId: company.companyId,
		companySettings: company.settings,
	};
}

export default connect(mapStateToProps)(App);
